import { cloneDeep } from 'lodash';
import { useRouter } from 'next/dist/client/router';
import Head from 'next/head';
import Link from 'next/link';
import {
  FunctionComponent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useThrottledScroll } from '../../hooks/useThrottledScroll';
import { Header2025, Header2025Links, Maybe } from '../../types/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../responsive';
import { Icon } from '../ui/icon';
import { Button } from '../ui/button';
import { GTMConversions } from '../scripts';
import { PromoBanner } from '../promo-banner';

export const Header: FunctionComponent<IHeader> = ({
  pageTitle,
  pageDescription,
  links,
  primary_button,
  secondary_button,
  disableHiding = true,
  promo_banner,
  forceBackground = false,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);

  const MINIMUM_SCROLL = 80;
  const TIMEOUT_DELAY = 100;

  useThrottledScroll(({ previousScrollTop, currentScrollTop }) => {
    const isScrolledDown = previousScrollTop < currentScrollTop;
    const isMinimumScrolled = currentScrollTop > MINIMUM_SCROLL;

    setHasShadow(currentScrollTop > 2);

    if (disableHiding) {
      return;
    }

    setTimeout(() => {
      setIsHidden(isScrolledDown && isMinimumScrolled);
    }, TIMEOUT_DELAY);
  });

  function getPageTitle() {
    if (
      pageTitle?.trim()?.startsWith('Mapped |') ||
      pageTitle?.trim()?.endsWith('| Mapped')
    ) {
      return pageTitle?.trim();
    }

    return `${pageTitle?.trim()} | Mapped`;
  }

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />

        <title>{getPageTitle()}</title>
        <meta
          name="description"
          content={pageDescription || 'A new perspective on IoT.'}
        />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={getPageTitle()} />
        <meta
          property="og:description"
          content={pageDescription || 'A new perspective on IoT.'}
        />
        <meta
          property="og:image"
          content="https://www.mapped.com/img/og-image.png"
        />
        <meta
          name="facebook-domain-verification"
          content="fznnmgxih7yiannqntpwylu8jhh8mi"
        />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={getPageTitle()} />
        <meta
          property="twitter:description"
          content={pageDescription || 'A new perspective on IoT.'}
        />
        <meta
          property="twitter:image"
          content="https://www.mapped.com/img/og-image.png"
        />
      </Head>

      <DesktopHeader
        isHidden={isHidden}
        hasShadow={hasShadow}
        links={links}
        primary_button={primary_button}
        secondary_button={secondary_button}
        promo_banner={promo_banner}
        forceBackground={forceBackground}
      />

      <MobileHeader
        isHidden={isHidden}
        hasShadow={hasShadow}
        links={links}
        primary_button={primary_button}
        secondary_button={secondary_button}
        promo_banner={promo_banner}
        forceBackground={forceBackground}
      />
    </>
  );
};

const DesktopHeader: FunctionComponent<IMobileHeaderProps> = ({
  isHidden,
  hasShadow,
  links,
  primary_button,
  secondary_button,
  promo_banner,
  forceBackground,
}) => {
  const { asPath: pathname, push } = useRouter();

  const [openLinkMenu, setOpenLinkMenu] = useState<string>('');

  function isActive(path?: string | null, strict?: boolean) {
    return pathname === path;
  }

  function openMenu(key?: string | null) {
    setOpenLinkMenu(key || '');
  }

  function closeMenu() {
    setOpenLinkMenu('');
  }

  return (
    <Container
      className="desktop-only"
      hasShadow={hasShadow}
      isHidden={isHidden}
      forceBackground={forceBackground}
    >
      <PromoBanner title={promo_banner?.title!} href={promo_banner?.href!} />

      <Content>
        <NextLink href="/">
          <Logo
            width="160"
            height="50"
            src="/img/header-logo.svg"
            alt="Mapped Logo"
          />
        </NextLink>

        <Nav>
          {links?.map((link, idx) => (
            <LinkRenderer
              key={link?.title + idx.toString()}
              link={link}
              isActive={isActive}
              isHidden={isHidden}
              openLinkMenu={openLinkMenu}
              openMenu={openMenu}
              closeMenu={closeMenu}
              push={push as any}
            />
          ))}

          <NextLink href={primary_button?.href || '/'} legacyBehavior passHref>
            <Button
              mSize="medium"
              style={{ marginLeft: '10px' }}
              onClick={() => {
                GTMConversions.signup();
              }}
            >
              {primary_button?.title}
            </Button>
          </NextLink>

          <NextLink
            href={secondary_button?.href || '/'}
            legacyBehavior
            passHref
          >
            <Button
              mSize="medium"
              mType="secondary"
              style={{ marginLeft: '10px' }}
            >
              {secondary_button?.title}
            </Button>
          </NextLink>
        </Nav>
      </Content>
    </Container>
  );
};

const MobileHeader: FunctionComponent<IMobileHeaderProps> = ({
  isHidden,
  hasShadow,
  links,
  primary_button,
  secondary_button,
  promo_banner,
  forceBackground,
}) => {
  const { pathname, push, asPath } = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const [openLinkMenu, setOpenLinkMenu] = useState<string>('');

  function isActive(path?: string | null, strict?: boolean) {
    return pathname === path;
  }

  function openMenu(key?: string | null) {
    setOpenLinkMenu(key || '');
  }

  function closeMenu() {
    setOpenLinkMenu('');
  }

  useEffect(() => {
    setIsOpen(false);
  }, [asPath]);

  useEffect(() => {
    document.body.classList.toggle('menu-open', isOpen);
  }, [isOpen]);

  return (
    <>
      <Container
        className="mobile-header mobile-only"
        isOpen={isOpen}
        isHidden={isHidden}
        hasShadow={hasShadow}
        forceBackground={forceBackground}
      >
        {!isOpen && (
          <PromoBanner
            title={promo_banner?.title!}
            href={promo_banner?.href!}
          />
        )}

        <Content>
          <NextLink href="/">
            <Logo
              src="/img/header-logo.svg"
              width="160"
              height="50"
              alt="Mapped Logo"
            />
          </NextLink>

          <Nav>
            <Toggle
              src={isOpen ? `/img/close-icon.svg` : `/img/menu-toggle.svg`}
              width="24"
              height="24"
              alt="menu toggle"
              onClick={() => setIsOpen(!isOpen)}
            />
          </Nav>
        </Content>

        {isOpen && (
          <MobileLinks>
            {links?.map((link, idx) => (
              <LinkRenderer
                key={link?.title + idx.toString()}
                link={link}
                isMobile={true}
                isActive={isActive}
                isHidden={isHidden}
                openLinkMenu={openLinkMenu}
                openMenu={openMenu}
                closeMenu={closeMenu}
                push={push as any}
              />
            ))}

            {!openLinkMenu && (
              <>
                <NextLink
                  href={primary_button?.href || '/'}
                  legacyBehavior
                  passHref
                >
                  <Button
                    mSize="medium"
                    onClick={() => {
                      GTMConversions.signup();
                    }}
                  >
                    {primary_button?.title}
                  </Button>
                </NextLink>

                <NextLink
                  href={secondary_button?.href || '/'}
                  legacyBehavior
                  passHref
                >
                  <Button mSize="medium" mType="secondary">
                    {secondary_button?.title}
                  </Button>
                </NextLink>
              </>
            )}
          </MobileLinks>
        )}
      </Container>
    </>
  );
};

const DropdownMenu: FunctionComponent<{ onClose: () => void; children?: React.ReactNode }> = ({
  children,
  onClose,
}) => {
  useLayoutEffect(() => {
    setTimeout(() => {
      document.body.addEventListener('click', onClose);
    }, 100);

    return () => document.body.removeEventListener('click', onClose);
  }, []);

  return <div className="dropdown-menu">{children}</div>;
};

const LinkRenderer: FunctionComponent<ILinkRenderer> = (props) => {
  const {
    openLinkMenu,
    isActive,
    isHidden,
    isMobile,
    openMenu,
    closeMenu,
    push,
    link,
  } = props;

  const { title, href, sub_links, ref_sub_linksConnection } = link ?? {};

  const isMenuOpen = openLinkMenu === href;

  const subLinks = useMemo(() => {
    const links = cloneDeep(sub_links || []);

    ref_sub_linksConnection?.edges?.forEach((edge) => {
      links.push({
        title: edge?.node?.meta?.title,
        href: edge?.node?.url,
      });
    });

    return links;
  }, [sub_links, ref_sub_linksConnection]);

  switch (true) {
    case !!subLinks?.length:
      return (
        <NavLink
          isActive={
            isMenuOpen ||
            isActive(href) ||
            subLinks?.some((s) => isActive(s?.href, true))
          }
          onClick={() => (!isMobile ? null : openMenu(href))}
          onMouseEnter={() => (isMobile ? null : openMenu(href))}
          onMouseLeave={() => (isMobile ? null : closeMenu())}
        >
          <span style={{ display: 'flex' }}>
            {title}
            <Icon
              name="arrow-down"
              style={{ marginRight: -7, marginLeft: 5 }}
            />
          </span>

          {isMenuOpen && !isHidden && (
            <DropdownMenu onClose={() => null}>
              {subLinks?.map((subLink) => (
                <NextLink href={subLink?.href} key={subLink?.href}>
                  <span
                    className={
                      isActive(subLink?.href, true) ? 'link active' : 'link'
                    }
                  >
                    {subLink?.title}
                  </span>
                </NextLink>
              ))}
            </DropdownMenu>
          )}
        </NavLink>
      );

    default:
      return (
        <NextLink href={href}>
          <NavLink isActive={isActive(href)}>{title}</NavLink>
        </NextLink>
      );
  }
};

export const HEADER_QUERY = /**gql */ `
  header: header_2025(uid: "blt9c25715665eb1db9") {
    promo_banner {
      title
      href
    }
    links {
      title
      href
      ref_sub_linksConnection {
        edges {
					node {
            __typename
						...on Vertical {
							url
              meta {
                title
              }
						}
            ...on VerticalAlt {
							url
              meta {
                title
              }
						}
					}
				}
      }
      sub_links {
        title
        href
      }
    }
    primary_button {
      title
      href
    }
    secondary_button {
      title
      href
    }
  }
`;

const NextLink = Link as any;

const Content = styled(ResponsiveContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Container = styled.header<{
  isOpen?: boolean;
  isHidden: boolean;
  hasShadow: boolean;
  forceBackground?: boolean;
}>`
  width: 100%;
  padding: 16px 0;
  z-index: 100;
  position: fixed;
  top: 0;
  background: transparent;
  transform: translateY(0);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  ${({ isHidden }) =>
    isHidden &&
    `
  transform: translateY(-110%);
  `}

  ${({ hasShadow, forceBackground, theme }) =>
    (hasShadow || forceBackground) &&
    `
    background: ${theme.colors.slate1};
  `}

  ${({ hasShadow }) =>
    hasShadow &&
    `
    box-shadow: 0 9px 9px -9px rgba(0, 0, 0, 0.23);
  `}

  &.mobile-only {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    &.desktop-only {
      display: none;
    }

    &.mobile-only {
      display: block;
    }
  }

  ${({ isOpen, theme }) =>
    isOpen &&
    `
    z-index: 100;
    background: ${theme.colors.slate1};
    transform: translateY(0) !important;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0;
    overflow: hidden;
    display: flex !important;
    flex-direction: column;

    ${Content} {
      padding-top: 16px;
    }
  `}
`;

const Logo = styled.img`
  width: 160px;
  cursor: pointer;
  pointer-events: all;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const NavLink = styled.div<{
  isActive?: boolean;
  isDropdownMenuOpen?: boolean;
}>`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  margin: 0 12px;
  white-space: nowrap;
  position: relative;
  top: 4px;
  border-bottom: 2px solid transparent;
  padding-bottom: 8px;
  cursor: pointer;
  outline: none;

  /* Tight desktop range (container 960–1140) shrinks links so the nav
     never wraps once there are many items. */
  ${mediaScreen('md', 'xl')} {
    font-size: 14px;
    margin: 0 8px;
  }

  &,
  &:visited {
    color: white;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.teal};
  }

  ${({ isActive, theme }) =>
    isActive &&
    `
    border-color: ${theme.colors.teal} !important;
  `}

  .icon-arrow-down {
    color: ${({ theme }) => theme.colors.teal};
  }

  .dropdown-menu {
    background: ${(props) => props.theme.colors.slate2};
    position: absolute;
    top: 34px;
    left: 0;
    padding: 7px 0;

    .link {
      font-size: 16px;
      color: white;
      padding: 7px 15px;
      cursor: pointer;
      white-space: nowrap;
      display: block;
      font-weight: 100;

      &:hover {
        background: rgba(255, 255, 255, 0.02);
      }

      &.active {
        font-weight: bold;
        background: rgba(255, 255, 255, 0.02);
      }
    }
  }
`;

const Toggle = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  pointer-events: all;
`;

const SubNavLink = styled.span`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 1.5px;
  font-weight: bold;
  margin-bottom: 24px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
`;

const MobileLinks = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 32px;
  position: relative;

  ${NavLink} {
    font-size: 29px;
    line-height: 32px;
    margin-bottom: 24px;
    margin-left: 0;
    border: none;

    span {
      align-items: center;
    }

    &:hover {
      opacity: 1;
    }
  }

  ${SubNavLink} {
    &:first-of-type {
      margin-top: 20px;
    }
  }

  .button {
    position: absolute;
    bottom: 32px;
    left: 32px;
    right: 32px;

    &.mButton-primary {
      bottom: 94px;
    }
  }

  .dropdown-menu {
    background: none;
    position: relative;
    top: 0;
    margin-top: 10px;

    .link {
      font-size: 16px;
      color: white;
      padding: 7px 0px;
      cursor: pointer;
      white-space: nowrap;
      display: block;
      font-weight: 100;
      background: none !important;
    }
  }
`;

interface IHeader {
  links?: Header2025['links'];
  primary_button?: Header2025['primary_button'];
  secondary_button?: Header2025['secondary_button'];
  pageTitle?: string | null;
  pageDescription?: string | null;
  disableHiding?: boolean;
  promo_banner?: Header2025['promo_banner'];
  forceBackground?: boolean;
}

interface IMobileHeaderProps {
  links?: Header2025['links'];
  primary_button?: Header2025['primary_button'];
  secondary_button?: Header2025['secondary_button'];
  isHidden: boolean;
  hasShadow: boolean;
  promo_banner?: Header2025['promo_banner'];
  forceBackground?: boolean;
}

interface ILinkRenderer {
  link: Maybe<Header2025Links>;
  openLinkMenu: string;
  isActive: (path?: string | null, strict?: boolean) => boolean;
  isHidden?: boolean;
  isMobile?: boolean;
  openMenu: (key?: string | null) => void;
  closeMenu: () => void;
  push: (path?: string | null) => void;
}
